/**
 * Admin: upload or remove the desktop app offered by the sidebar's "Download app" button, which
 * every signed-in account sees.
 */

import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { APP_DOWNLOAD_URL, fetchAppInfo, removeApp, uploadApp } from "../api";
import { formatBytes, formatTimestamp } from "../format";
import { confirmDialog } from "./Dialog";

export function AppUploadPanel({ csrfToken }: { csrfToken: string }): JSX.Element {
  const queryClient = useQueryClient();
  const info = useQuery({ queryKey: ["app-info"], queryFn: fetchAppInfo });
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [manifest, setManifest] = useState("");
  const manifestInput = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ["app-info"] });

  // A quick look at update.json before uploading, so an obviously mismatched pair is caught here
  // instead of after a long upload. The server re-checks everything, including the SHA-256.
  const parsed = useMemo((): { version: string; size: number | null } | { error: string } | null => {
    if (!manifest.trim()) return null;
    try {
      const json = JSON.parse(manifest) as { version?: unknown; size?: unknown };
      if (typeof json.version !== "string") return { error: "update.json has no version." };
      return { version: json.version, size: typeof json.size === "number" ? json.size : null };
    } catch {
      return { error: "That update.json is not valid JSON." };
    }
  }, [manifest]);
  const manifestError =
    parsed && "error" in parsed
      ? parsed.error
      : parsed && file && parsed.size !== null && parsed.size !== file.size
        ? `update.json is for a ${formatBytes(parsed.size)} file, but this .exe is ${formatBytes(file.size)}. Pick the pair from the same build.`
        : null;
  const ready = Boolean(file && parsed && !("error" in parsed) && !manifestError);

  const upload = async (): Promise<void> => {
    if (!file || !parsed || "error" in parsed || manifestError) return;
    if (info.data?.available) {
      const ok = await confirmDialog({
        title: `Release v${parsed.version}?`,
        message: `This replaces v${info.data.version ?? "?"}. Installed apps will update to v${parsed.version} the next time they start.`,
        confirmLabel: "Release",
      });
      if (!ok) return;
    }
    setStatus(null);
    setProgress(0);
    try {
      await uploadApp(file, manifest, csrfToken, setProgress);
      setStatus({ kind: "ok", text: `Released v${parsed.version}. Installed apps will update when they next start.` });
      setFile(null);
      setManifest("");
      if (manifestInput.current) manifestInput.current.value = "";
      if (fileInput.current) fileInput.current.value = "";
      await refresh();
    } catch (caught) {
      setStatus({ kind: "error", text: caught instanceof Error ? caught.message : "Upload failed." });
    } finally {
      setProgress(null);
    }
  };

  const remove = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: "Remove the app download?",
      message: "The Download app button disappears from everyone's sidebar until you upload a new file.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await removeApp(csrfToken);
      setStatus({ kind: "ok", text: "The app download has been removed." });
      await refresh();
    } catch (caught) {
      setStatus({ kind: "error", text: caught instanceof Error ? caught.message : "Could not remove it." });
    }
  };

  const current = info.data;
  const uploading = progress !== null;

  return (
    <section className="users-panel">
      <h2>Desktop app</h2>

      <div className="app-current">
        {current?.available ? (
          <>
            <div>
              <b>{current.fileName}</b>
              {current.version && <span className="nav-count app-version">v{current.version}</span>}
              <div className="users-note">
                {formatBytes(current.bytes ?? null)} · uploaded by {current.uploadedBy} on{" "}
                {current.uploadedAt ? formatTimestamp(current.uploadedAt) : "?"}
              </div>
              <div className="app-autoupdate" data-on={current.signed === true}>
                {current.signed
                  ? "✓ Auto-update: installed apps will update to this version"
                  : "⚠ Auto-update off: this was uploaded without update.json. Upload the release again with it."}
              </div>
            </div>
            <span className="grow" />
            <a className="btn" href={APP_DOWNLOAD_URL} download={current.fileName}>
              Download
            </a>
            <button className="btn" type="button" data-variant="danger" onClick={() => void remove()}>
              Remove
            </button>
          </>
        ) : (
          <span className="users-note">
            {info.isLoading ? "Loading…" : "No app uploaded yet. Upload one and a Download app button appears in everyone's sidebar."}
          </span>
        )}
      </div>

      <div className="filterbar">
        <input
          ref={fileInput}
          type="file"
          hidden
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        <button className="btn" type="button" disabled={uploading} onClick={() => fileInput.current?.click()}>
          {file ? "Change file" : "Choose file"}
        </button>
        <span className="users-note app-file">{file ? `${file.name} (${formatBytes(file.size)})` : "No file chosen"}</span>
        <input
          ref={manifestInput}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={async (event) => setManifest((await event.target.files?.[0]?.text()) ?? "")}
        />
        <button className="btn" type="button" disabled={uploading} onClick={() => manifestInput.current?.click()}>
          {parsed && !("error" in parsed) ? `update.json ✓ v${parsed.version}` : "Choose update.json"}
        </button>
        <button className="btn" data-variant="primary" type="button" disabled={!ready || uploading} onClick={() => void upload()}>
          {uploading
            ? `Uploading ${Math.round((progress ?? 0) * 100)}%`
            : parsed && !("error" in parsed)
              ? `Release v${parsed.version}`
              : "Release"}
        </button>
      </div>
      <p className="users-note">
        Upload both files from the same build: <b>PrismSMP Staff Portal.exe</b> and its <b>update.json</b>.
      </p>
      {manifestError && (
        <div className="login-error" role="alert">
          {manifestError}
        </div>
      )}

      {uploading && (
        <div className="xp-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((progress ?? 0) * 100)}>
          <div className="xp-fill" style={{ width: `${(progress ?? 0) * 100}%` }} />
        </div>
      )}

      {status && (
        <div className={status.kind === "error" ? "login-error" : "evidence-ok"} role="status">
          {status.text}
        </div>
      )}
    </section>
  );
}
